import { useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, FormGroup, Stack } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import axiosInstance from '../../../utils/axios';
import { APIS } from '../../../utils/serviceUrls';
import { SuccessStory } from '../../../@types/successStory';
import { FormProvider, RHFTextField, RHFSwitch, RHFSelect } from '../../../components/hook-form';
import { UploadMultiFile } from '../../../components/upload';
import TextMaxLine from '../../../components/TextMaxLine';
import { SuccessStorySchema } from './index';

type SuccessStoryNewEditFormProps = {
  isEdit: boolean;
  currentStory?: SuccessStory;
  handleClose(): void;
};

const SuccessStoryNewEditForm = ({
  isEdit,
  currentStory,
  handleClose,
}: SuccessStoryNewEditFormProps) => {
  const { defaultValues, NewSuccessStorySchema } = SuccessStorySchema(currentStory);
  const { enqueueSnackbar } = useSnackbar();

  const methods = useForm<any>({
    resolver: yupResolver(NewSuccessStorySchema),
    defaultValues,
  });

  const {
    reset,
    watch,
    setValue,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = methods;

  const values = watch();
  const isActivity = values.type === 'activity';

  const uploadImage = async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const response = await axiosInstance.post(APIS.UPLOAD.MEDIA, form);
    return response.data;
  };

  const onSubmit = async (data: any) => {
    if (!data.name_ar) delete data?.name_ar;
    if (!data.title) delete data?.title;
    if (!data.title_ar) delete data?.title_ar;
    if (!data.story_ar) delete data?.story_ar;
    if (data.type === 'activity' && data.date) {
      data.date = new Date(data.date).toISOString();
    } else {
      delete data?.date;
    }
    try {
      if (currentStory)
        await axiosInstance.patch(APIS.SUCCESS_STORIES.PATCH(currentStory._id as string), data);
      else await axiosInstance.post(APIS.SUCCESS_STORIES.ADD, data);
      reset();
      enqueueSnackbar(!isEdit ? 'Create success!' : 'Update success!');
      handleClose();
    } catch (err) {
      if (err?.message) {
        enqueueSnackbar(err?.message, { variant: 'error' });
      } else {
        enqueueSnackbar(!isEdit ? 'Unable to create Story!' : 'Unable to edit Story', {
          variant: 'error',
        });
      }
    }
  };

  useEffect(() => {
    console.log('Errors', errors);
  }, [errors]);

  return (
    <Stack height={'100%'} style={{ overflow: 'auto' }}>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <TextMaxLine marginBottom={'0px'} marginTop={'0px'} fontSize={'22px'}>
          {isEdit ? ' Edit Success Story' : ' Create Success Story'}
        </TextMaxLine>
        <Stack direction={'column'} justifyContent={'space-between'} height={'auto'}>
          <Stack spacing={3} mt={2}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 1,
                rowGap: 1,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                alignItems: 'center',
              }}
            >
              <RHFSelect name="type" label="Type">
                <option value="story">Story</option>
                <option value="activity">Activity</option>
              </RHFSelect>
              {isActivity && (
                <RHFTextField
                  name="date"
                  label="Event date"
                  type="date"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            </Box>

            <Box
              sx={{
                display: 'grid',
                columnGap: 1,
                rowGap: 1,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField name="name" label={isActivity ? 'Event title' : 'Name'} />
              <RHFTextField
                name="name_ar"
                label={isActivity ? 'Arabic event title' : 'Arabic Name'}
              />
              <RHFTextField name="title" label={isActivity ? 'Location / tagline' : 'Title'} />
              <RHFTextField
                name="title_ar"
                label={isActivity ? 'Arabic location / tagline' : 'Arabic Title'}
              />
            </Box>

            <FormGroup
              sx={{
                display: 'grid',
                columnGap: 1,
                rowGap: 1,
              }}
            >
              <RHFTextField multiline minRows={4} maxRows={8} name="story" label="Story" />
              <RHFTextField
                multiline
                minRows={4}
                maxRows={8}
                name="story_ar"
                label="Arabic Story"
              />
            </FormGroup>

            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 1,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                alignItems: 'center',
              }}
            >
              <RHFTextField name="order" label="Order" type="number" />
              <RHFSwitch name="active" label="Active" />
            </Box>

            <Stack spacing={1}>
              <TextMaxLine sx={{ fontSize: '16px', fontWeight: '700' }}>Images</TextMaxLine>
              <UploadMultiFile
                accept={{ 'image/*': [] }}
                files={values.images || []}
                label={'Select Or Drop Images '}
                error={!!errors.images}
                onDropAccepted={async (files) => {
                  const res = await uploadImage(files[0]);
                  setValue('images', [...(values.images || []), res?.url]);
                }}
                onRemove={(file) =>
                  setValue(
                    'images',
                    (values.images || []).filter((u: string) => u !== file)
                  )
                }
              />
            </Stack>
          </Stack>

          <Stack alignItems="flex-end" sx={{ mt: 3 }}>
            <LoadingButton
              style={{ width: '200px', height: '50px' }}
              type="submit"
              variant="contained"
              loading={isSubmitting}
            >
              {!isEdit ? 'Create Story' : 'Save Changes'}
            </LoadingButton>
          </Stack>
        </Stack>
      </FormProvider>
    </Stack>
  );
};

export default SuccessStoryNewEditForm;
